import React, { useEffect, useState, useCallback } from "react";
import {
  Loader2, GripVertical, X, Plus, RefreshCw, ChevronUp, ChevronDown,
  Zap, MessageSquare, Mic, Image as ImageIcon, Video, Languages, Music2, Search,
  CheckCircle2, AlertTriangle, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const CAP_ICON = {
  chat: MessageSquare, voice: Mic, image: ImageIcon, video: Video,
  translation: Languages, music: Music2, search: Search,
};
const CAP_LABEL = {
  chat: "Chat / Text",
  voice: "Voice / TTS",
  image: "Image Gen",
  video: "Video Gen",
  translation: "Translation",
  music: "Music / SFX",
  search: "Web Search",
};

function ChainRow({ entry, eligible, idx, total, onMove, onRemove }) {
  const meta = eligible.find((p) => p.key === entry.provider_key) || {};
  const isManaged = entry.mode === "managed";
  return (
    <div
      data-testid={`chain-row-${entry.provider_key}`}
      className="flex items-center gap-2 rounded-md border border-line bg-bg-elevated p-2 hover:border-brand-amber/30 transition"
    >
      <GripVertical className="h-4 w-4 text-ink-muted shrink-0" />
      <div className="font-mono text-[10px] text-ink-muted w-6 shrink-0">#{idx + 1}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink-primary truncate">{meta.label || entry.provider_key}</div>
        <div className="text-[11px] text-ink-muted truncate">{meta.vendor || "—"}</div>
      </div>
      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${isManaged ? "bg-brand-amber/10 text-brand-amber" : "bg-emerald-500/10 text-emerald-300"}`}>
        {isManaged ? "Credits" : "Your Key"}
      </span>
      <div className="flex gap-0.5 shrink-0">
        <button
          onClick={() => onMove(idx, idx - 1)}
          disabled={idx === 0}
          data-testid={`move-up-${entry.provider_key}`}
          className="h-7 w-7 grid place-items-center rounded text-ink-muted hover:text-brand-amber hover:bg-brand-amber/10 disabled:opacity-30 disabled:hover:bg-transparent transition"
          title="Move up"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onMove(idx, idx + 1)}
          disabled={idx === total - 1}
          data-testid={`move-down-${entry.provider_key}`}
          className="h-7 w-7 grid place-items-center rounded text-ink-muted hover:text-brand-amber hover:bg-brand-amber/10 disabled:opacity-30 disabled:hover:bg-transparent transition"
          title="Move down"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onRemove(idx)}
          data-testid={`remove-${entry.provider_key}`}
          className="h-7 w-7 grid place-items-center rounded text-ink-muted hover:text-brand-onair hover:bg-brand-onair/10 transition"
          title="Remove from chain"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function AddProviderMenu({ eligible, chain, onAdd }) {
  const [open, setOpen] = useState(false);
  const used = new Set(chain.map((c) => c.provider_key));
  const available = eligible.filter((p) => !used.has(p.key));
  if (available.length === 0) return null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chain-add-btn"
        className="w-full text-xs inline-flex items-center justify-center gap-1 rounded-md border border-dashed border-brand-amber/40 text-brand-amber py-2 hover:bg-brand-amber/5 transition"
      >
        <Plus className="h-3.5 w-3.5" /> Add provider
      </button>
      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-line bg-bg-surface shadow-xl"
          data-testid="chain-add-menu"
        >
          {available.map((p) => (
            <button
              key={p.key}
              onClick={() => { onAdd(p); setOpen(false); }}
              data-testid={`add-${p.key}`}
              className="w-full text-left px-3 py-2 text-xs hover:bg-bg-elevated flex items-center justify-between gap-2 border-b border-line/40 last:border-b-0"
            >
              <span className="truncate">{p.label}</span>
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0 ${p.mode === "managed" ? "bg-brand-amber/10 text-brand-amber" : "bg-emerald-500/10 text-emerald-300"}`}>
                {p.mode === "managed" ? "Credits" : "Your Key"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CapabilityCard({ capData, onMutated }) {
  const Icon = CAP_ICON[capData.capability] || Zap;
  const [chain, setChain] = useState(capData.chain || []);
  const [savedChain, setSavedChain] = useState(capData.chain || []);
  const [resolution, setResolution] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setChain(capData.chain || []);
    setSavedChain(capData.chain || []);
  }, [capData]);

  const dirty = JSON.stringify(chain) !== JSON.stringify(savedChain);

  const move = (from, to) => {
    if (to < 0 || to >= chain.length) return;
    const next = [...chain];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setChain(next);
  };
  const remove = (idx) => {
    const next = [...chain];
    next.splice(idx, 1);
    setChain(next);
  };
  const add = (provider) => {
    setChain([...chain, { provider_key: provider.key, mode: provider.mode }]);
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/providers/routing/${capData.capability}`, {
        chain: chain.map((c) => ({ provider_key: c.provider_key, mode: c.mode })),
      });
      toast.success(`${CAP_LABEL[capData.capability]} chain saved`);
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const reset = async () => {
    if (!await appConfirm(`Reset ${CAP_LABEL[capData.capability]} to BroadcastFlow's default chain?`)) return;
    setBusy("reset");
    try {
      await api.delete(`/providers/routing/${capData.capability}`);
      toast.success("Reset to default");
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(""); }
  };

  const probeResolve = async () => {
    setBusy("resolve");
    setResolution(null);
    try {
      const r = await api.post("/providers/routing/resolve", { capability: capData.capability });
      setResolution(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed");
    } finally { setBusy(""); }
  };

  const isCustom = capData.source === "custom";

  return (
    <div
      className="rounded-xl border border-line bg-bg-surface p-4"
      data-testid={`cap-card-${capData.capability}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="rounded-md p-2 bg-brand-amber/10 text-brand-amber shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-bold leading-tight">{CAP_LABEL[capData.capability]}</div>
          <div className="text-[11px] text-ink-muted flex items-center gap-1">
            <span className={`font-mono uppercase tracking-wider ${isCustom ? "text-brand-amber" : "text-ink-muted"}`}>
              {isCustom ? "Custom" : "Default"}
            </span>
            <span>·</span>
            <span>{chain.length} provider{chain.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        {isCustom && (
          <button
            onClick={reset}
            disabled={busy === "reset"}
            data-testid={`reset-${capData.capability}`}
            className="text-[10px] text-ink-muted hover:text-brand-onair inline-flex items-center gap-1"
            title="Reset to BroadcastFlow defaults"
          >
            {busy === "reset" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Reset
          </button>
        )}
      </div>

      {chain.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-bg-elevated p-4 text-center text-xs text-ink-muted">
          No providers in this chain. Add one below — calls to {capData.capability} will fail until you do.
        </div>
      ) : (
        <div className="space-y-1.5 mb-3">
          {chain.map((e, i) => (
            <ChainRow
              key={e.provider_key}
              entry={e}
              eligible={capData.eligible}
              idx={i}
              total={chain.length}
              onMove={move}
              onRemove={remove}
            />
          ))}
        </div>
      )}

      <AddProviderMenu eligible={capData.eligible} chain={chain} onAdd={add} />

      <div className="mt-3 flex items-center justify-between gap-2">
        <button
          onClick={probeResolve}
          disabled={busy === "resolve"}
          data-testid={`resolve-${capData.capability}`}
          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-line hover:border-brand-blue/40 hover:text-brand-blue transition"
          title="Preview which provider would be picked right now"
        >
          {busy === "resolve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Test resolution
        </button>
        <button
          onClick={save}
          disabled={!dirty || busy === "save"}
          data-testid={`save-${capData.capability}`}
          className="text-xs px-4 py-1.5 rounded-md font-bold bg-brand-amber text-bg disabled:opacity-30 hover:bg-brand-amber/90 transition inline-flex items-center gap-1"
        >
          {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {dirty ? "Save chain" : "Saved"}
        </button>
      </div>

      {resolution && (
        <div
          className="mt-3 rounded-md border border-line bg-bg-elevated p-3"
          data-testid={`resolution-${capData.capability}`}
        >
          <div className="flex items-center gap-2 mb-2">
            {resolution.picked ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-brand-onair" />
            )}
            <div className="text-xs">
              {resolution.picked ? (
                <>
                  Would route to <strong className="text-emerald-300">{resolution.picked.provider_key}</strong>
                  <span className="text-ink-muted ml-1">({resolution.picked.mode === "managed" ? "credits" : "your key"})</span>
                </>
              ) : (
                <span className="text-brand-onair">No eligible provider — calls would fail</span>
              )}
            </div>
          </div>
          {resolution.trace?.length > 0 && (
            <div className="space-y-1">
              {resolution.trace.map((t, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] font-mono">
                  <span className={t.ok ? "text-emerald-400 shrink-0" : "text-ink-muted shrink-0"}>
                    {t.ok ? "✓" : "·"}
                  </span>
                  <span className="text-ink-primary shrink-0">{t.provider_key}</span>
                  <span className="text-ink-muted truncate">— {t.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function RoutingPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/providers/routing");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load routing");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return (
    <div className="p-10 text-center font-mono text-xs text-ink-muted">
      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-brand-amber" /> Loading routing chains…
    </div>
  );
  if (!data) return null;

  return (
    <div data-testid="routing-panel" className="animate-fade-up">
      <div className="rounded-xl border border-brand-blue/30 bg-brand-blue/5 p-4 mb-4 flex items-start gap-3">
        <Zap className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
        <div className="text-xs text-ink-secondary leading-relaxed">
          <strong className="text-brand-blue">Hybrid routing &amp; failover.</strong> For each capability,
          order the providers you want BroadcastFlow to try. If the first one is offline (alerts marked
          it red), admin-disabled, or its BYOK key isn&rsquo;t configured, the call falls over to the
          next one automatically. Mix your own keys with managed providers freely.
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {data.capabilities.map((c) => (
          <CapabilityCard key={c.capability} capData={c} onMutated={load} />
        ))}
      </div>
    </div>
  );
}
